// Type definitions
/**
 * Must stay in step with what the backend actually writes: the nine detectors in
 * functions-insights/func-agentInsights.ts, the three behaviour-triggered types
 * in func-createAgentInsight.ts, and agent_gift.
 *
 * The last four were missing while the backend was producing them, so those
 * insights fell back to a generic label, were absent from the type filter, and
 * went uncounted in the by-type statistics.
 */
export const INSIGHT_TYPES = [
	'refinance_opportunity', 'rate_alert', 'payment_stress', 'calculator_engagement',
	'first_time_buyer', 'high_value_prospect', 'pre_approval_ready', 'inactive_user', 'agent_gift',
	'debt_consolidation', 'home_equity_opportunity', 'market_timing', 'financial_milestone'
] as const;
export type InsightType = typeof INSIGHT_TYPES[number];

export const INSIGHT_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type InsightPriority = typeof INSIGHT_PRIORITIES[number];
export type InsightStatus = 'new' | 'viewed' | 'contacted' | 'in_progress' | 'converted' | 'resolved' | 'dismissed' | 'archived';
export type CustomerStatus = 'prospect' | 'active' | 'inactive' | 'converted';
export type CustomerSource = 'app' | 'website' | 'referral' | 'import' | 'other';

export interface Agent {
	id: string;
	email: string;
	firstName?: string; // Optional - some agents may only have 'name'
	lastName?: string; // Optional - some agents may only have 'name'
	name?: string; // Optional - alternative to firstName/lastName
	displayName?: string;
	phone?: string;
	companyName?: string;
	company?: string; // Alternative to companyName
	companyLogo?: string;
	profileImage?: string;
	role: 'agent' | 'master_agent' | 'super_agent';
	isActive: boolean;
	createdAt: Date;
	updatedAt: Date;
	lastLoginAt?: Date;
	firebaseUserId?: string; // Optional field to link to Firebase Auth user ID
	assignedCustomers?: string[]; // Array of customer IDs
	territory?: string;
	licenseNumber?: string;
	specialties?: string[];
	// Notification preferences
	notificationPreferences?: {
		newCustomerAssignment?: boolean;
		customerRedistribution?: boolean;
		newInsightGenerated?: boolean;
		highPriorityInsight?: boolean;
		customerAssessmentCompleted?: boolean;
		leadScoreChanged?: boolean;
		customerStatusChanged?: boolean;
		insightExpiring?: boolean;
		weeklyReports?: boolean;
	};
}

export interface Customer {
	id: string;
	userId?: string; // Link to user account if they have an app account
	firstName: string;
	lastName: string;
	email: string;
	phone?: string;
	assignedAgentId?: string;
	status: CustomerStatus;
	leadScore?: number;
	source?: CustomerSource;
	leadSource?: string; // Deprecated, use source
	createdAt: Date;
	updatedAt: Date;
	lastContactAt?: Date;
	lastActivityAt?: Date;
	lastInteractionType?: 'call' | 'email' | 'meeting' | 'note';
	totalInteractions?: number;
	mortgageBalance?: number;
	homeValue?: number;
	creditScore?: number;
	income?: number;
	notes?: string;
	data?: {
		mortgageRate?: number;
		debtToIncomeRatio?: number;
		isFirstTimeBuyer?: boolean;
		[key: string]: any;
	};
	tags?: string[];
	profileData?: {
		income?: number;
		creditScore?: number;
		employmentStatus?: string;
		homeOwnershipStatus?: string;
		location?: string;
	};
}

export interface CustomerInfo {
	firstName: string;
	lastName: string;
	email: string;
	phone?: string | null;
	bestTimeToContact?: string;
	preferredContactMethod?: string;
	timezone?: string | null;
}

/**
 * Financial snapshot the backend attaches to every agent insight.
 * Source of truth: functions-insights/src/user/insightHandlers/func-agentInsights.ts
 * (CustomerInfo['financialContext'], built by getCustomerInfo + enhanceFinancialContext).
 * Every field is optional - the backend omits anything it could not derive.
 */
export interface InsightFinancialContext {
	creditScore?: number;
	annualIncome?: number;
	debtToIncomeRatio?: number;
	employmentStatus?: string;
	currentMortgageBalance?: number;
	currentMortgageRate?: number;
	currentMonthlyPayment?: number;
	homeValue?: number;
	propertyAddress?: {
		city?: string;
		state?: string;
		zipCode?: string;
	};
}

/**
 * What caused the backend to generate this insight.
 * `timestamp` arrives as a Firestore Timestamp and is converted to Date by InsightService.
 */
export interface InsightTriggerData {
	source: string;
	timestamp?: Date;
	context?: Record<string, any>;
}

export interface AgentInsight {
	id: string;
	/**
	 * Owner of the insight. The backend writes `_userId` on every agentInsights
	 * document; `userId` is a portal-only legacy alias that nothing populates.
	 */
	_userId?: string;
	/** @deprecated The backend never writes this - read `_userId` instead. */
	userId?: string;
	/** Backend-generated natural key, e.g. `refinance_opp_<uid>_<epoch>`. */
	insightId?: string;
	/** Agent this insight is routed to. Backend field name is snake_case. */
	agent_id?: string | null;
	customerId?: string;
	assignedAgentId?: string;
	type: InsightType;
	priority: InsightPriority;
	leadScore: number;
	title: string;
	description: string;
	/** Suggested next steps written by the insight generator. */
	actionItems?: string[];
	contactSuggestion: string;
	status: InsightStatus;
	viewed?: boolean;
	viewedAt?: Date;
	/** Backend retention flag. The scheduled cleanup job archives/deletes on this, not on `status`. */
	archived?: boolean;
	contactAttempts?: number;
	createdAt: Date;
	updatedAt?: Date;
	expiresAt: Date;
	resolvedAt?: Date;
	resolvedBy?: string;
	customerInfo?: CustomerInfo; // Embedded customer information
	financialContext?: InsightFinancialContext;
	triggerData?: InsightTriggerData;
	/** Insight-type-specific qualification numbers (loan amount, savings, readiness score...). */
	qualificationData?: Record<string, any>;
	data?: Record<string, any>; // Additional insight-specific data
	/**
	 * @deprecated Not an insight field. These belong to `InsightTrend` and only exist
	 * here because InsightsPage stores trend rows in an AgentInsight[] state.
	 * Switch that state to `InsightTrend[]` and these can be removed.
	 */
	count?: number;
	/** @deprecated See `count`. */
	trend?: 'up' | 'down' | 'stable';
}

/** Aggregated insight-type trend row returned by InsightService.getTrendingInsights. */
export interface InsightTrend {
	type: InsightType;
	count: number;
	trend: 'up' | 'down' | 'stable';
}

// Additional type for customer insights (simplified version)
export interface CustomerInsight extends AgentInsight {
	customerName?: string;
	customerEmail?: string;
	customerPhone?: string;
}

export interface AgentDashboardStats {
	totalCustomers: number;
	activeInsights: number;
	urgentInsights: number;
	conversionsThisWeek: number;
	averageLeadScore: number;
	responseRate: number;
} 